import { getCurrentConfig, getApiScope } from '../config';
import { DEFAULT_HEADERS } from '../config/constants';

interface ThresholdApiResponse {
  success: boolean;
  message: string;
  timestamp: string;
  data: {
    thresholds: Array<{
      threshold_id: number;
      asset_code: string;
      green_threshold: number;
      yellow_threshold: number;
      amber_threshold: number;
      minflow: number | null;
      maxflow: number | null;
      is_active: boolean;
      created_date: string;
      modified_date: string;
    }>;
    hash: string;
    count: number;
    cache_info: {
      provided_hash: string | null;
      current_hash: string;
      data_changed: boolean;
    };
  };
  error: null | string;
}

interface ThresholdMap {
  [assetCode: string]: {
    green: number;
    yellow: number;
    amber: number;
    minflow: number;
    maxflow: number;
  };
}

class ThresholdService {
  private static readonly STORAGE_KEY = 'checkpoint_thresholds';
  private static readonly HASH_KEY = 'checkpoint_thresholds_hash';
  private static readonly CACHE_TIMESTAMP_KEY = 'checkpoint_thresholds_timestamp';
  private static readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
  private static readonly MIN_REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutes
  private static refreshTimer: NodeJS.Timeout | null = null;
  private static lastFetchTime = 0;

  private getBaseURL(): string {
    const config = getCurrentConfig();
    const baseUrl = config?.apiBaseUrl || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    return baseUrl;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const baseUrl = this.getBaseURL();
    
    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    const cleanEndpoint = endpoint.replace(/^\//, '');
    const url = `${cleanBaseUrl}/${cleanEndpoint}`;
    
    const headers: Record<string, string> = {
      ...DEFAULT_HEADERS,
    };

    const config = getCurrentConfig();
    const authEnabled = config.auth.enableAuthentication;
    
    if (authEnabled) {
      try {
        const { msalInstance } = await import('../contexts/AuthContext');
        const accounts = msalInstance.getAllAccounts();
        
        if (accounts.length > 0) {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: [getApiScope()], // Use scope from .env
            account: accounts[0]
          });
          headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
          console.log('ThresholdService: Auth enabled, using token');
        } else {
          console.warn('ThresholdService: Auth enabled but no accounts available');
        }
      } catch (error) {
        console.warn('ThresholdService: Auth enabled but token acquisition failed:', error);
      }
    } else {
      console.log('ThresholdService: Auth disabled');
    }

    if (options?.headers) {
      Object.assign(headers, options.headers);
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  private static transformApiResponse(apiData: ThresholdApiResponse['data']): ThresholdMap {
    const thresholds: ThresholdMap = {};
    
    apiData.thresholds
      .filter(threshold => threshold.is_active)
      .forEach(threshold => {
        thresholds[threshold.asset_code] = {
          green: threshold.green_threshold,
          yellow: threshold.yellow_threshold,
          amber: threshold.amber_threshold,
          minflow: threshold.minflow !== null ? threshold.minflow : 5,  // Default to 5 if null
          maxflow: threshold.maxflow !== null ? threshold.maxflow : 20  // Default to 20 if null
        };
      });
    
    return thresholds;
  }

  private static isCacheValid(): boolean {
    try {
      const timestamp = localStorage.getItem(this.CACHE_TIMESTAMP_KEY);
      if (!timestamp) return false;
      
      const cacheTime = parseInt(timestamp);
      const now = Date.now();
      
      return (now - cacheTime) < this.CACHE_DURATION;
    } catch (error) {
      return false;
    }
  }

  async fetchThresholds(): Promise<ThresholdMap | null> {
    try {
      console.log('ThresholdService: Fetching thresholds from API...');
      
      const data: ThresholdApiResponse = await this.request<ThresholdApiResponse>('asset-thresholds/', {
        method: 'GET'
      });
      
      if (data.success && data.data) {
        const thresholds = ThresholdService.transformApiResponse(data.data);
        
        localStorage.setItem(ThresholdService.STORAGE_KEY, JSON.stringify(thresholds));
        localStorage.setItem(ThresholdService.HASH_KEY, data.data.hash);
        localStorage.setItem(ThresholdService.CACHE_TIMESTAMP_KEY, Date.now().toString());
        
        console.log('ThresholdService: Thresholds fetched and cached:', {
          count: Object.keys(thresholds).length,
          hash: data.data.hash,
          timestamp: data.timestamp
        });
        
        return thresholds;
      }
      
      return null;
    } catch (error) {
      console.error('ThresholdService: Failed to fetch thresholds:', error);
      return null;
    }
  }

  static getCachedThresholds(): ThresholdMap | null {
    try {
      if (!this.isCacheValid()) {
        console.log('ThresholdService: Cache expired, clearing...');
        this.clearCache();
        return null;
      }
      
      const cached = localStorage.getItem(this.STORAGE_KEY);
      if (cached) {
        const thresholds = JSON.parse(cached);
        console.log('ThresholdService: Using cached thresholds:', Object.keys(thresholds).length);
        return thresholds;
      }
      return null;
    } catch (error) {
      console.error('ThresholdService: Failed to read cached thresholds:', error);
      return null;
    }
  }

  static clearCache(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.HASH_KEY);
    localStorage.removeItem(this.CACHE_TIMESTAMP_KEY);
    console.log('ThresholdService: Cache cleared');
  }

  async getThresholds(): Promise<ThresholdMap> {
    try {
      const cached = ThresholdService.getCachedThresholds();
      if (cached) {
        return cached;
      }

      console.log('ThresholdService: No valid cache, fetching from API...');
      const fetched = await this.fetchThresholds();
      if (fetched) {
        ThresholdService.lastFetchTime = Date.now();
        return fetched;
      }

      // NEW: Check if localStorage has ANY threshold data from other checkpoint
      console.log('ThresholdService: API failed, checking for any stored threshold data...');
      try {
        const anyStoredData = localStorage.getItem(ThresholdService.STORAGE_KEY);
        if (anyStoredData) {
          const storedThresholds = JSON.parse(anyStoredData);
          if (storedThresholds && Object.keys(storedThresholds).length > 0) {
            console.log('ThresholdService: Using stored thresholds from other checkpoint:', Object.keys(storedThresholds).length);
            return storedThresholds;
          }
        }
      } catch (storageError) {
        console.warn('ThresholdService: Failed to read stored threshold data:', storageError);
      }

      console.warn('ThresholdService: No stored data available, using default thresholds');
      return this.getDefaultThresholds();
      
    } catch (error) {
      console.error('ThresholdService: Error in getThresholds:', error);
      return this.getDefaultThresholds();
    }
  }

  async refreshThresholds(): Promise<ThresholdMap> {
    try {
      console.log('ThresholdService: Force refreshing thresholds...');
      ThresholdService.clearCache();
      
      const fetched = await this.fetchThresholds();
      if (fetched) {
        ThresholdService.lastFetchTime = Date.now();
        return fetched;
      }
      
      console.warn('ThresholdService: Refresh failed, using defaults');
      return this.getDefaultThresholds();
      
    } catch (error) {
      console.error('ThresholdService: Error in refreshThresholds:', error);
      return this.getDefaultThresholds();
    }
  }

  static startPeriodicRefresh(refreshCallback?: (thresholds: ThresholdMap) => void): void {
    this.stopPeriodicRefresh();
    
    console.log('ThresholdService: Starting periodic refresh (15 min intervals)');
    
    this.refreshTimer = setInterval(async () => {
      const now = Date.now();
      
      if (now - this.lastFetchTime >= this.MIN_REFRESH_INTERVAL) {
        try {
          console.log('ThresholdService: Automatic refresh triggered');
          const service = new ThresholdService();
          const thresholds = await service.refreshThresholds();
          this.lastFetchTime = now;
          
          if (refreshCallback) {
            refreshCallback(thresholds);
          }
        } catch (error) {
          console.error('ThresholdService: Automatic refresh failed:', error);
        }
      }
    }, this.MIN_REFRESH_INTERVAL);
  }

  static stopPeriodicRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
      console.log('ThresholdService: Periodic refresh stopped');
    }
  }

  private getDefaultThresholds(): ThresholdMap {
    return {
      // CP-1A thresholds
      "t1-1a_s1-1_z01": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s1-1_z02": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s1-1_z03": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s1-1_z04": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s1-1_z05": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s1-1_z06": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s1-2_z01": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s1-2_z02": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s1-2_z03": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s1-2_z04": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s1-2_z05": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s1-2_z06": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s2_z02": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s3_a01": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s3_a02": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s3_a03": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s3_a04": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s4_a01": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s4_a02": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s4_a03": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1a_s5_a01": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      
      // CP-1B thresholds  
      "t1-1b_s1-1_z01": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1b_s1-1_z02": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1b_s1-1_z03": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1b_s1-1_z04": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1b_s1-2_z01": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1b_s1-2_z02": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1b_s1-2_z03": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1b_s1-2_z04": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1b_s2_a01": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1b_s2_a02": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1b_s2_a03": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1b_s2_a04": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 },
      "t1-1b_s2_a05": { green: 20, yellow: 25, amber: 30, minflow: 5, maxflow: 20 }
    };
  }
}

export const thresholdService = new ThresholdService();

export const ThresholdServiceStatic = {
  getCachedThresholds: ThresholdService.getCachedThresholds.bind(ThresholdService),
  clearCache: ThresholdService.clearCache.bind(ThresholdService),
  startPeriodicRefresh: ThresholdService.startPeriodicRefresh.bind(ThresholdService),
  stopPeriodicRefresh: ThresholdService.stopPeriodicRefresh.bind(ThresholdService)
};
